import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Minus, Trash, ShoppingBag, Tag, X } from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartSheet({ cart, open, onOpenChange, removeFromCart, updateQuantity }) {
  const [loading, setLoading] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoLoading, setPromoLoading] = useState(false);

  const total = promoApplied ? promoApplied.price : cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(`${API}/promo/validate`, { code: promoCode });
      setPromoApplied(res.data);
      toast.success(`Promo applied: ${res.data.description}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid promo code");
      setPromoApplied(null);
    } finally {
      setPromoLoading(false);
    }
  };

  const handleRemovePromo = () => {
    setPromoApplied(null);
    setPromoCode("");
  };

  const handleCheckout = async () => {
    if (cart.length === 0 && !promoApplied) return;

    setLoading(true);
    try {
      let body;
      if (promoApplied) {
        body = {
          items: [],
          origin_url: window.location.origin,
          promo_code: promoApplied.code,
        };
      } else {
        body = {
          items: cart.map(item => ({
            pack_id: item.packId,
            bars: item.bars.map(b => ({ product_id: b.id, quantity: b.quantity })),
          })),
          origin_url: window.location.origin,
        };
      }

      const response = await axios.post(`${API}/checkout`, body);

      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error("Checkout error:", err);
      toast.error("Checkout failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="flex flex-col w-full sm:max-w-md"
        style={{ backgroundColor: '#0C0C0C' }}
      >
        <SheetHeader>
          <SheetTitle className="font-heading text-2xl font-light tracking-tight" style={{ color: '#F0EDE5' }}>
            Your Cart
          </SheetTitle>
          <SheetDescription className="font-body text-sm" style={{ color: '#9A9A9A' }}>
            {itemCount === 0 ? "Your cart is empty" : `${itemCount} pack${itemCount > 1 ? 's' : ''} in your cart`}
          </SheetDescription>
        </SheetHeader>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto py-6 space-y-4" data-testid="cart-items-container">
          {cart.length === 0 && !promoApplied ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 opacity-40">
              <ShoppingBag size={48} weight="thin" style={{ color: '#9A9A9A' }} />
              <p className="font-body text-sm" style={{ color: '#9A9A9A' }}>Nothing here yet</p>
            </div>
          ) : (
            cart.map((item) => (
              <div
                key={item.id}
                className="p-4 border"
                style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}
                data-testid={`cart-item-${item.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-heading text-lg font-medium" style={{ color: '#F0EDE5' }}>
                      {item.packName}
                    </h4>
                    <p className="font-heading text-lg" style={{ color: '#FF5A36' }}>
                      ${item.price.toFixed(2)}
                    </p>
                  </div>
                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="p-1 transition-colors"
                    style={{ color: '#666' }}
                    onMouseEnter={(e) => e.currentTarget.style.color = '#FF5A36'}
                    onMouseLeave={(e) => e.currentTarget.style.color = '#666'}
                    data-testid={`cart-remove-${item.id}`}
                  >
                    <Trash size={16} weight="regular" />
                  </button>
                </div>
                {/* Bar details */}
                <div className="space-y-2">
                  {item.bars && item.bars.map((bar, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <img src={bar.image} alt={bar.name} className="w-8 h-8 object-cover" />
                      <span className="font-body text-xs" style={{ color: '#9A9A9A' }}>
                        {bar.quantity}x {bar.name}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Promo + Checkout Footer */}
        <div className="border-t pt-6 space-y-4" style={{ borderColor: '#2A2A2A' }}>
          {/* Promo Code Input */}
          {!promoApplied ? (
            <div className="flex gap-2">
              <input
                type="text"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApplyPromo()}
                placeholder="Promo code"
                className="flex-1 px-4 py-2.5 text-sm font-body border outline-none focus:border-[#FF5A36] transition-colors"
                style={{ backgroundColor: '#141414', borderColor: '#2A2A2A', color: '#F0EDE5' }}
                data-testid="promo-code-input"
              />
              <button
                onClick={handleApplyPromo}
                disabled={promoLoading || !promoCode.trim()}
                className="px-4 py-2.5 text-xs font-accent font-semibold tracking-wider uppercase text-white transition-all duration-300 hover:brightness-110 disabled:opacity-40"
                style={{ backgroundColor: '#FF5A36' }}
                data-testid="apply-promo-btn"
              >
                {promoLoading ? "..." : "Apply"}
              </button>
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#141414' }}>
              <div className="flex items-center gap-2">
                <Tag size={16} weight="bold" style={{ color: '#FF5A36' }} />
                <div>
                  <p className="font-accent text-xs font-bold tracking-wide uppercase" style={{ color: '#FF5A36' }}>
                    {promoApplied.code}
                  </p>
                  <p className="font-body text-xs" style={{ color: '#9A9A9A' }}>
                    {promoApplied.description}
                  </p>
                </div>
              </div>
              <button
                onClick={handleRemovePromo}
                className="p-1 transition-colors hover:text-[#FF5A36]"
                style={{ color: '#666' }}
                data-testid="remove-promo-btn"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          )}

          {/* Promo deal details */}
          {promoApplied && (
            <div className="p-3 border" style={{ borderColor: '#2A2A2A', backgroundColor: '#0A1A0A' }}>
              <p className="font-body text-xs mb-2" style={{ color: '#22c55e' }}>
                Deal applied: 1x Split Scrub + 1x Total Scrub
              </p>
              <div className="flex items-center justify-between">
                <span className="font-body text-xs line-through" style={{ color: '#666' }}>$16.00</span>
                <span className="font-heading text-lg" style={{ color: '#22c55e' }}>${promoApplied.price.toFixed(2)}</span>
              </div>
            </div>
          )}

          {(cart.length > 0 || promoApplied) && (
            <>
              <div className="flex items-center justify-between">
                <span className="font-body text-sm" style={{ color: '#9A9A9A' }}>
                  Total (shipping included)
                </span>
                <span className="font-heading text-2xl font-light" style={{ color: '#F0EDE5' }}>
                  ${total.toFixed(2)}
                </span>
              </div>
              <button
                onClick={handleCheckout}
                disabled={loading}
                className="w-full py-4 text-sm font-accent font-semibold tracking-wider uppercase text-white transition-all duration-300 hover:brightness-110 disabled:opacity-50"
                style={{ backgroundColor: '#FF5A36' }}
                data-testid="checkout-button"
              >
                {loading ? "Processing..." : "Checkout with Stripe"}
              </button>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
